import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';

// Add custom CSS for modal centering
const modalStyles = `
    .location-modal-dialog {
        max-width: 80vw !important;
        width: 80vw !important;
        margin: auto !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
    }
    
    .location-modal-dialog .modal-content {
        width: 100% !important;
        max-height: 90vh !important;
        margin: auto !important;
    }
`;

// Inject styles if not already present
if (!document.getElementById('location-modal-styles')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'location-modal-styles';
    styleSheet.textContent = modalStyles;
    document.head.appendChild(styleSheet);
}

const LocationMapModal = ({ show, onHide, location, title = "Location", zIndex = 1050 }) => {
    const [mapLoaded, setMapLoaded] = useState(false);
    const [map, setMap] = useState(null);

    useEffect(() => {
        // Load Google Maps script if not already loaded
        if (!window.google) {
            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            
            if (!apiKey) {
                console.error('Google Maps API key is missing!');
                return;
            }
            
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = () => setMapLoaded(true);
            script.onerror = (error) => {
                console.error('Failed to load Google Maps script:', error);
            };
            document.head.appendChild(script);
        } else {
            setMapLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (mapLoaded && show && location && location.lat && location.lng) {
            initializeMap();
        }
    }, [mapLoaded, show, location]);

    const initializeMap = () => {
        const mapElement = document.getElementById('location-map');
        if (!mapElement) return;

        const mapInstance = new window.google.maps.Map(mapElement, {
            center: { lat: location.lat, lng: location.lng },
            zoom: 15,
            mapTypeId: 'roadmap'
        });

        // Add marker
        const marker = new window.google.maps.Marker({
            position: { lat: location.lat, lng: location.lng },
            map: mapInstance,
            title: location.address || 'Clock Event Location',
            icon: {
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                    <svg width="32" height="45" viewBox="0 0 32 45" xmlns="http://www.w3.org/2000/svg">
                        <!-- Drop shadow -->
                        <ellipse cx="16" cy="42" rx="8" ry="3" fill="rgba(0,0,0,0.3)"/>
                        
                        <!-- Main pin body with gradient -->
                        <defs>
                            <linearGradient id="pinGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" style="stop-color:#ff4757;stop-opacity:1" />
                                <stop offset="100%" style="stop-color:#c44569;stop-opacity:1" />
                            </linearGradient>
                            <filter id="pinShadow" x="-50%" y="-50%" width="200%" height="200%">
                                <feDropShadow dx="2" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.4)"/>
                            </filter>
                        </defs>
                        
                        <!-- Pin shape -->
                        <path d="M16 2 C23.732 2 30 8.268 30 16 C30 20.5 25 28 16 40 C7 28 2 20.5 2 16 C2 8.268 8.268 2 16 2 Z" 
                                fill="url(#pinGradient)" 
                                stroke="#fff" 
                                stroke-width="2" 
                                filter="url(#pinShadow)"/>
                        
                        <!-- Inner circle -->
                        <circle cx="16" cy="16" r="6" fill="#fff" opacity="0.9"/>
                        
                        <!-- Center dot -->
                        <circle cx="16" cy="16" r="3" fill="#2f3542"/>
                    </svg>
                `),
                scaledSize: new window.google.maps.Size(32, 45),
                anchor: new window.google.maps.Point(16, 40)
            }
        });

        // Add info window if address is available
        if (location.address) {
            const infoWindow = new window.google.maps.InfoWindow({
                content: `
                    <div style="padding: 10px;">
                        <h6>${title}</h6>
                        <p style="margin: 0;"><strong>Address:</strong> ${location.address}</p>
                        <p style="margin: 0;"><strong>Coordinates:</strong> ${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}</p>
                        ${location.accuracy ? `<p style=\"margin: 0;\"><strong>Accuracy:</strong> ±${location.accuracy}m</p>` : ''}
                        ${location.timestamp ? `<p style=\"margin: 0;\"><strong>Time:</strong> ${new Date(location.timestamp).toLocaleDateString('en-GB')} ${new Date(location.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>` : ''}
                    </div>
                `
            });

            marker.addListener('click', () => {
                infoWindow.open(mapInstance, marker);
            });

            // Auto-open info window
            infoWindow.open(mapInstance, marker);
        }

        setMap(mapInstance);
    };

    return (
        <Modal 
            show={show} 
            onHide={onHide} 
            size="xl" 
            centered
            dialogClassName="location-modal-dialog"
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: zIndex
            }}
        >
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '0' }}>
                {location ? (
                    <div 
                        id="location-map" 
                        style={{ 
                            height: '70vh', 
                            width: '100%',
                            minHeight: '500px'
                        }}
                    >
                        {!mapLoaded && (
                            <div style={{ 
                                height: '100%', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                backgroundColor: '#f8f9fa'
                            }}>
                                Loading map...
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        No location data available
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default LocationMapModal;